import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons"
import { Paper, IconButton, Typography, styled } from "@mui/material"
import { FaIcon } from "@skistarab/mui-theme"
import { useTranslation } from "react-i18next"

type CounterProps = {
  value: number
  onIncrement: () => void
  onDecrement: () => void
  maxValue?: number
  minValue?: number
  disabled?: boolean
  label: string
}

const ButtonWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>(({ theme, disabled }) => ({
  borderRadius: "50%",
  backgroundColor: disabled ? "#FFFFFF40" : theme.palette.common.white,
}))

const CounterIconButton = styled(IconButton)({
  ":disabled": {
    color: "inherit",
  },
})

const Counter = ({
  value,
  onIncrement,
  onDecrement,
  maxValue = 100,
  minValue = 0,
  disabled,
  label,
}: CounterProps) => {
  const { t } = useTranslation()
  const canIncrement = !disabled && value < maxValue
  const canDecrement = !disabled && value > minValue

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        bgcolor: "grey.100",
        p: "2px",
        borderRadius: "24px",
      }}
    >
      <ButtonWrapper disabled={!canDecrement}>
        <CounterIconButton
          onClick={onDecrement}
          disabled={!canDecrement}
          aria-label={t("counter.decrease", { label })}
        >
          <FaIcon icon={faMinus} fontSize="inherit" />
        </CounterIconButton>
      </ButtonWrapper>

      <Typography
        variant="body1"
        sx={{
          mx: 2,
          textAlign: "center",
        }}
        aria-live="polite"
      >
        {value}
      </Typography>
      <ButtonWrapper>
        <IconButton
          onClick={onIncrement}
          disabled={!canIncrement}
          aria-label={t("counter.increase", { label })}
        >
          <FaIcon icon={faPlus} fontSize="inherit" />
        </IconButton>
      </ButtonWrapper>
    </Paper>
  )
}

export default Counter
